/**
 * ProjectionComparisonChart Component
 *
 * Line chart comparing projected vs actual values by month.
 * Shows Gross, Expenses, Operating Profit, and optionally Units.
 * Uses dual Y-axes: dollars on left, units on right.
 */

import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { MonthlyTotals, getMonthValue, MONTH_NAMES } from '../../types';

interface ChartDataPoint {
  month: string;
  monthNum: number;
  projectedGross: number | null;
  actualGross: number | null;
  projectedExpenses: number | null;
  actualExpenses: number | null;
  projectedProfit: number | null;
  actualProfit: number | null;
  projectedUnits?: number | null;
  actualUnits?: number | null;
}

interface ProjectionComparisonChartProps {
  title: string;
  projectedGross: MonthlyTotals;
  actualGross: MonthlyTotals | null;
  projectedExpenses: MonthlyTotals;
  actualExpenses: MonthlyTotals | null;
  projectedProfit: MonthlyTotals;
  actualProfit: MonthlyTotals | null;
  // Units are optional (only for Location Summary, New, Used departments)
  projectedUnits?: MonthlyTotals;
  actualUnits?: MonthlyTotals | null;
  showUnits?: boolean;
  // For OTHERS department, only show gross
  grossOnly?: boolean;
}

const formatCurrency = (value: number): string => {
  if (value === 0) return '$0';
  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  if (absValue >= 1000000) {
    return `${sign}$${(absValue / 1000000).toFixed(1)}M`;
  } else if (absValue >= 1000) {
    return `${sign}$${(absValue / 1000).toFixed(0)}K`;
  }
  return `${sign}$${absValue.toFixed(0)}`;
};

const formatUnits = (value: number): string => {
  return value.toLocaleString();
};

// Custom tooltip with all metrics
const CustomTooltip = ({ active, payload, label, showUnits, grossOnly }: any) => {
  if (!active || !payload || !payload.length) return null;

  // Group by metric type
  const grossData = payload.filter((p: any) => p.dataKey.includes('Gross'));
  const expensesData = payload.filter((p: any) => p.dataKey.includes('Expenses'));
  const profitData = payload.filter((p: any) => p.dataKey.includes('Profit'));
  const unitsData = payload.filter((p: any) => p.dataKey.includes('Units'));

  const renderMetricGroup = (title: string, data: any[], isUnits: boolean = false) => {
    if (data.length === 0) return null;
    return (
      <div className="mb-2">
        <p className="text-xs font-semibold text-gray-500 mb-1">{title}</p>
        {data.map((entry: any, index: number) => (
          <div key={index} className="flex items-center gap-2 text-sm">
            <div
              className="w-3 h-0.5"
              style={{
                backgroundColor: entry.color,
                borderStyle: entry.dataKey.includes('projected') ? 'dashed' : 'solid',
              }}
            />
            <span className="text-gray-600 text-xs">
              {entry.dataKey.includes('projected') ? 'Proj' : 'Act'}:
            </span>
            <span className="font-medium text-xs" style={{ color: entry.color }}>
              {entry.value !== null
                ? isUnits
                  ? formatUnits(entry.value)
                  : formatCurrency(entry.value)
                : '—'}
            </span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 min-w-[140px]">
      <p className="font-semibold text-gray-900 mb-2 text-sm">{label}</p>
      {renderMetricGroup('Gross', grossData)}
      {!grossOnly && renderMetricGroup('Expenses', expensesData)}
      {!grossOnly && renderMetricGroup('Profit', profitData)}
      {showUnits && renderMetricGroup('Units', unitsData, true)}
    </div>
  );
};

// Line colors - light for projected (dashed), dark for actual (solid)
const LINE_COLORS = {
  projectedGross: '#93c5fd',    // blue-300 (light)
  actualGross: '#2563eb',       // blue-600 (dark)
  projectedExpenses: '#fca5a5', // red-300 (light)
  actualExpenses: '#dc2626',    // red-600 (dark)
  projectedProfit: '#86efac',   // green-300 (light)
  actualProfit: '#16a34a',      // green-600 (dark)
  projectedUnits: '#d1d5db',    // gray-300 (light)
  actualUnits: '#4b5563',       // gray-600 (dark)
};

export const ProjectionComparisonChart: React.FC<ProjectionComparisonChartProps> = ({
  title,
  projectedGross,
  actualGross,
  projectedExpenses,
  actualExpenses,
  projectedProfit,
  actualProfit,
  projectedUnits,
  actualUnits,
  showUnits = false,
  grossOnly = false,
}) => {
  // Build chart data for all 12 months
  const chartData: ChartDataPoint[] = MONTH_NAMES.map((month, i) => {
    const monthNum = i + 1;

    // Get projected values (always available)
    const projGross = getMonthValue(projectedGross, monthNum);
    const projExp = grossOnly ? 0 : getMonthValue(projectedExpenses, monthNum);
    const projProfit = grossOnly ? 0 : getMonthValue(projectedProfit, monthNum);
    const projUnits = showUnits && projectedUnits ? getMonthValue(projectedUnits, monthNum) : undefined;

    // Get actual values (may be null/zero for future months)
    const actGross = actualGross ? getMonthValue(actualGross, monthNum) : null;
    const actExp = grossOnly ? null : (actualExpenses ? getMonthValue(actualExpenses, monthNum) : null);
    const actProfit = grossOnly ? null : (actualProfit ? getMonthValue(actualProfit, monthNum) : null);
    const actUnits = showUnits && actualUnits ? getMonthValue(actualUnits, monthNum) : undefined;

    return {
      month,
      monthNum,
      projectedGross: projGross,
      actualGross: actGross === 0 ? null : actGross, // Treat 0 as no data for future months
      projectedExpenses: grossOnly ? null : projExp,
      actualExpenses: grossOnly ? null : (actExp === 0 ? null : actExp),
      projectedProfit: grossOnly ? null : projProfit,
      actualProfit: grossOnly ? null : (actProfit === 0 ? null : actProfit),
      projectedUnits: projUnits,
      actualUnits: actUnits === 0 ? null : actUnits,
    };
  });

  // Calculate Y-axis domains
  const allDollarValues = chartData.flatMap(d => [
    d.projectedGross,
    d.actualGross,
    d.projectedExpenses,
    d.actualExpenses,
    d.projectedProfit,
    d.actualProfit,
  ]).filter((v): v is number => v !== null && v !== undefined);

  const allUnitValues = showUnits
    ? chartData.flatMap(d => [d.projectedUnits, d.actualUnits])
        .filter((v): v is number => v !== null && v !== undefined)
    : [];

  const minDollar = Math.min(0, ...allDollarValues);
  const maxDollar = Math.max(...allDollarValues) * 1.1; // 10% padding
  const maxUnits = allUnitValues.length > 0 ? Math.max(...allUnitValues) * 1.1 : 100;

  return (
    <div className="bg-gray-50 rounded-lg p-4 mt-2 mb-4">
      <h4 className="text-sm font-semibold text-gray-700 mb-3">{title}</h4>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={chartData}
            margin={{ top: 5, right: showUnits ? 60 : 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 10, fill: '#6b7280' }}
              tickLine={{ stroke: '#d1d5db' }}
              axisLine={{ stroke: '#d1d5db' }}
            />
            <YAxis
              yAxisId="dollars"
              tickFormatter={formatCurrency}
              tick={{ fontSize: 10, fill: '#6b7280' }}
              tickLine={{ stroke: '#d1d5db' }}
              axisLine={{ stroke: '#d1d5db' }}
              width={65}
              domain={[minDollar, maxDollar]}
            />
            {showUnits && (
              <YAxis
                yAxisId="units"
                orientation="right"
                tickFormatter={formatUnits}
                tick={{ fontSize: 10, fill: '#6b7280' }}
                tickLine={{ stroke: '#d1d5db' }}
                axisLine={{ stroke: '#d1d5db' }}
                width={50}
                domain={[0, maxUnits]}
              />
            )}
            <Tooltip content={<CustomTooltip showUnits={showUnits} grossOnly={grossOnly} />} />
            <Legend
              wrapperStyle={{ paddingTop: '10px', fontSize: '11px' }}
              formatter={(value: string) => {
                const labels: Record<string, string> = {
                  projectedGross: 'Gross (Proj)',
                  actualGross: 'Gross (Act)',
                  projectedExpenses: 'Exp (Proj)',
                  actualExpenses: 'Exp (Act)',
                  projectedProfit: 'Profit (Proj)',
                  actualProfit: 'Profit (Act)',
                  projectedUnits: 'Units (Proj)',
                  actualUnits: 'Units (Act)',
                };
                return <span className="text-xs text-gray-600">{labels[value] || value}</span>;
              }}
            />

            {/* Gross Lines */}
            <Line
              yAxisId="dollars"
              type="monotone"
              dataKey="projectedGross"
              stroke={LINE_COLORS.projectedGross}
              strokeWidth={2}
              strokeDasharray="6 4"
              dot={false}
              activeDot={{ r: 4 }}
            />
            <Line
              yAxisId="dollars"
              type="monotone"
              dataKey="actualGross"
              stroke={LINE_COLORS.actualGross}
              strokeWidth={2}
              dot={{ r: 3, fill: LINE_COLORS.actualGross }}
              activeDot={{ r: 5 }}
              connectNulls={false}
            />

            {/* Expenses Lines */}
            {!grossOnly && (
              <>
                <Line
                  yAxisId="dollars"
                  type="monotone"
                  dataKey="projectedExpenses"
                  stroke={LINE_COLORS.projectedExpenses}
                  strokeWidth={2}
                  strokeDasharray="6 4"
                  dot={false}
                  activeDot={{ r: 4 }}
                />
                <Line
                  yAxisId="dollars"
                  type="monotone"
                  dataKey="actualExpenses"
                  stroke={LINE_COLORS.actualExpenses}
                  strokeWidth={2}
                  dot={{ r: 3, fill: LINE_COLORS.actualExpenses }}
                  activeDot={{ r: 5 }}
                  connectNulls={false}
                />
              </>
            )}

            {/* Profit Lines */}
            {!grossOnly && (
              <>
                <Line
                  yAxisId="dollars"
                  type="monotone"
                  dataKey="projectedProfit"
                  stroke={LINE_COLORS.projectedProfit}
                  strokeWidth={2}
                  strokeDasharray="6 4"
                  dot={false}
                  activeDot={{ r: 4 }}
                />
                <Line
                  yAxisId="dollars"
                  type="monotone"
                  dataKey="actualProfit"
                  stroke={LINE_COLORS.actualProfit}
                  strokeWidth={2}
                  dot={{ r: 3, fill: LINE_COLORS.actualProfit }}
                  activeDot={{ r: 5 }}
                  connectNulls={false}
                />
              </>
            )}

            {/* Units Lines (optional) */}
            {showUnits && (
              <>
                <Line
                  yAxisId="units"
                  type="monotone"
                  dataKey="projectedUnits"
                  stroke={LINE_COLORS.projectedUnits}
                  strokeWidth={2}
                  strokeDasharray="6 4"
                  dot={false}
                  activeDot={{ r: 4 }}
                />
                <Line
                  yAxisId="units"
                  type="monotone"
                  dataKey="actualUnits"
                  stroke={LINE_COLORS.actualUnits}
                  strokeWidth={2}
                  dot={{ r: 3, fill: LINE_COLORS.actualUnits }}
                  activeDot={{ r: 5 }}
                  connectNulls={false}
                />
              </>
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
